import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_INPUT_LENGTH = 3;

const createSessionToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2);
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
};

const sanitizeQuery = (value) => value.replace(/\s+/g, ' ').trim();

const buildAutocompleteUrl = (query, sessionToken) => {
  const params = new URLSearchParams({
    input: query,
  });

  if (sessionToken) {
    params.set('sessionToken', sessionToken);
  }

  return `/api/maps/autocomplete?${params.toString()}`;
};

const buildDetailsUrl = (placeId, sessionToken) => {
  const params = new URLSearchParams({ placeId });

  if (sessionToken) {
    params.set('sessionToken', sessionToken);
  }

  return `/api/maps/place-details?${params.toString()}`;
};

const useAddressAutocomplete = ({
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minimumInputLength = DEFAULT_MIN_INPUT_LENGTH,
} = {}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const sessionTokenRef = useRef(createSessionToken());
  const debounceTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = createSessionToken();
  }, []);

  const cancelOngoingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelOngoingRequest();

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [cancelOngoingRequest]);

  const fetchSuggestions = useCallback(
    async (query) => {
      const sanitized = sanitizeQuery(query);

      if (sanitized.length < minimumInputLength) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      cancelOngoingRequest();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setIsLoading(true);
        setError('');

        const data = await fetchJson(
          buildAutocompleteUrl(sanitized, sessionTokenRef.current),
          controller.signal,
        );

        setSuggestions(Array.isArray(data.predictions) ? data.predictions : []);
        setIsDropdownOpen(true);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        console.error('Address autocomplete suggestions error', err);
        setSuggestions([]);
        setError('Unable to fetch address suggestions.');
      } finally {
        setIsLoading(false);
      }
    },
    [cancelOngoingRequest, minimumInputLength],
  );

  const handleInputChange = useCallback(
    (value) => {
      setInputValue(value);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      const sanitized = sanitizeQuery(value);

      if (sanitized.length < minimumInputLength) {
        setSuggestions([]);
        setIsDropdownOpen(false);
        setIsLoading(false);
        return;
      }

      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(sanitized);
      }, debounceMs);
    },
    [debounceMs, fetchSuggestions, minimumInputLength],
  );

  const selectSuggestion = useCallback(
    async (suggestion) => {
      if (!suggestion?.placeId) {
        return null;
      }

      cancelOngoingRequest();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setIsLoading(true);
        setError('');

        const details = await fetchJson(
          buildDetailsUrl(suggestion.placeId, sessionTokenRef.current),
          controller.signal,
        );

        resetSessionToken();
        setSuggestions([]);
        setIsDropdownOpen(false);

        return details;
      } catch (err) {
        if (err.name === 'AbortError') {
          return null;
        }

        console.error('Address autocomplete place details error', err);
        setError('Unable to fetch address details.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [cancelOngoingRequest, resetSessionToken],
  );

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    error,
    isDropdownOpen,
    handleInputChange,
    selectSuggestion,
    closeDropdown,
    resetSessionToken,
  };
};

export default useAddressAutocomplete;
